import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ListingTypeToggle } from '@/components/filters/ListingTypeToggle';
import { lookupListingByAddress } from '@/services/api';
import { PlacesSearchBar } from '@/components/map/PlacesSearchBar';

export const HeroSearch = () => {
  const navigate = useNavigate();
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [listingType, setListingType] = useState('Sale');

  const withListingType = (params) => {
    params.set('status', 'A');
    params.set('listingType', listingType);
    return params;
  };

  const handlePlaceSelect = (payload) => setSelectedPlace(payload);
  const handlePlaceInputChange = (val) => { if (!val) setSelectedPlace(null); };

  const handleCurrentLocation = (payload) => {
    const params = new URLSearchParams();
    if (payload?.city) params.set('city', payload.city);
    else if (payload?.zip) params.set('zip', payload.zip);
    else if (payload?.location) {
      params.set('lat', payload.location.lat);
      params.set('lng', payload.location.lng);
      params.set('zoom', 12);
    }
    navigate(`/map?${withListingType(params)}`);
  };

  const handleSearch = async () => {
    if (!selectedPlace) {
      navigate(`/map?${withListingType(new URLSearchParams())}`);
      return;
    }

    const { isPreciseAddress, viewport: _vp, location, ...patch } = selectedPlace;

    if (isPreciseAddress) {
      if (location) {
        const match = await lookupListingByAddress(location.lat, location.lng);
        if (match) { navigate(`/listing/${match.id}`); return; }
        const p = withListingType(new URLSearchParams({
          lat: String(location.lat),
          lng: String(location.lng),
          zoom: '16',
        }));
        navigate(`/map?${p}`);
      }
      return;
    }

    const params = new URLSearchParams();
    Object.entries(patch).forEach(([k, v]) => { if (v !== '' && v != null) params.set(k, String(v)); });
    navigate(`/map?${withListingType(params)}`);
  };

  return (
    <div
      data-testid="home-hero-search-panel"
      className="w-[min(1080px,calc(100vw-1.5rem))] rounded-2xl border border-slate-200/90 bg-white p-3 shadow-2xl shadow-slate-900/15 sm:p-4"
    >
      <form
        onSubmit={(e) => { e.preventDefault(); handleSearch(); }}
        className="flex flex-col gap-3"
      >
        <div className="flex items-center justify-between gap-3">
          <ListingTypeToggle
            value={listingType}
            onChange={setListingType}
            className="w-full sm:w-auto [&>button]:flex-1 sm:[&>button]:flex-none sm:[&>button]:px-4 sm:[&>button]:py-2"
          />
        </div>

        <div className="flex flex-col gap-2.5 sm:flex-row sm:items-stretch sm:gap-3">
          <div data-testid="hero-location-autocomplete" className="min-w-0 flex-1">
            <PlacesSearchBar
              size="hero"
              onSelect={handlePlaceSelect}
              onCurrentLocation={handleCurrentLocation}
              onInputChange={handlePlaceInputChange}
              className="w-full"
            />
          </div>
          <Button
            type="submit"
            data-testid="hero-search-submit"
            className="h-14 shrink-0 rounded-xl bg-blue-600 px-8 text-base font-semibold text-white shadow-sm hover:bg-blue-700 sm:min-w-[9.5rem]"
          >
            <Search size={20} className="mr-2" aria-hidden="true" />
            Search
          </Button>
        </div>
      </form>
    </div>
  );
};
